import MuiButton, { type ButtonProps } from "@mui/material/Button";
import { Link } from "@tanstack/react-router";

import type { Router } from "@/routes/routeTree";

type RoutePaths = keyof Router["routesByPath"];
type ParamsForTo<TTo extends RoutePaths> =
  Router["routesByPath"][TTo]["types"]["params"];

type BaseProps = Pick<
  ButtonProps,
  | "children"
  | "variant"
  | "onClick"
  | "size"
  | "startIcon"
  | "disabled"
  | "type"
  | "color"
  | "endIcon"
  | "className"
  | "sx"
>;

type WithTo<TTo extends RoutePaths> = BaseProps & {
  to: TTo;
} & (keyof ParamsForTo<TTo> extends never
    ? ParamsForTo<TTo>
    : undefined extends ParamsForTo<TTo>
      ? { params?: ParamsForTo<TTo> }
      : { params: ParamsForTo<TTo> });

type WithoutTo = BaseProps & { component?: void; to?: void; params?: void };

type Props<TTo extends RoutePaths | void = void> = TTo extends RoutePaths
  ? WithTo<TTo>
  : WithoutTo;

export const Button = <TTo extends RoutePaths | void = void>(
  props: Props<TTo>,
) => {
  if ("to" in props && typeof props.to === "string") {
    const { to, ...rest } = props;
    const params = "params" in props ? props.params : undefined;

    return <MuiButton component={Link} to={to} params={params} {...rest} />;
  }
  return <MuiButton {...props} />;
};
